/**
 * AccessRequest wizard (Round 1.4 — A5.4).
 *
 * Multi-step form that submits an approval row via POST /api/v1/approvals
 * with `asset_type='access_request'`. The control-plane resolves the
 * required reviewer steps from ACCESS_REQUEST_RULES.
 *
 * Steps:
 *   1. What — pick asset type + asset_id (preloadable via query string from
 *      the My Access "request" links).
 *   2. Why — risk tier + free-text justification.
 *   3. Review — confirm + submit.
 *
 * After submit succeeds we navigate the user to /govern/approvals so they
 * can see the row land in the queue.
 */
import { useEffect, useMemo, useState, type JSX } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, ArrowRight, Check, ShieldQuestion } from 'lucide-react';

import {
  ApiError,
  useApprovalSubmit,
  useCatalogServers,
  useCatalogTools,
  type ApprovalSubmitInput,
} from '@/api/index.js';
import { ActionButton } from '@/components/console/ActionButton.js';
import { PageHeader } from '@/components/console/PageHeader.js';
import { Required, RequiredLegend } from '@/components/console/Required.js';
import { SectionCard } from '@/components/console/SectionCard.js';

type Step = 1 | 2 | 3;

type AssetTypeOption = ApprovalSubmitInput['asset_type'];

const ASSET_TYPES: { value: AssetTypeOption; label: string; help: string }[] = [
  {
    value: 'access_request',
    label: 'Tool / catalog access',
    help: 'Ask to be added to a WG that grants access to a specific tool or server.',
  },
  {
    value: 'server',
    label: 'Register a new server',
    help: 'A server you own is queued for governance review before it goes live.',
  },
  {
    value: 'owner_transfer',
    label: 'Owner transfer',
    help: 'Move a server from one owning WG to another.',
  },
  {
    value: 'policy_exception',
    label: 'Policy exception',
    help: 'Temporary exception to a policy rule (e.g., elevated scope for a one-off task).',
  },
];

const RISK_TIERS = ['low', 'medium', 'high', 'critical'] as const;

export default function AccessRequest(): JSX.Element {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const submit = useApprovalSubmit();
  const tools = useCatalogTools();
  const servers = useCatalogServers();

  const [step, setStep] = useState<Step>(1);
  const [assetType, setAssetType] = useState<AssetTypeOption>(
    (params.get('asset_type') as AssetTypeOption | null) ?? 'access_request',
  );
  const [assetId, setAssetId] = useState<string>(params.get('asset_id') ?? '');
  const [riskTier, setRiskTier] = useState<(typeof RISK_TIERS)[number]>('medium');
  const [riskNotes, setRiskNotes] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);

  // Preload from query string on mount.
  useEffect(() => {
    const qsType = params.get('asset_type') as AssetTypeOption | null;
    const qsId = params.get('asset_id');
    if (qsType) setAssetType(qsType);
    if (qsId) setAssetId(qsId);
  }, [params]);

  const assetSuggestions = useMemo(() => {
    if (assetType === 'server' || assetType === 'owner_transfer') {
      return (servers.data?.items ?? []).map((s) => ({ id: s.id, label: `${s.name} · ${s.slug}` }));
    }
    if (assetType === 'access_request') {
      const requestable = (tools.data?.requestable ?? []).map((r) => ({
        id: r.tool.id,
        label: `${r.tool.name} (missing ${r.missing_wg ?? '?'})`,
      }));
      const available = (tools.data?.available ?? []).map((t) => ({
        id: t.id,
        label: `${t.name} (already available)`,
      }));
      return [...requestable, ...available];
    }
    return [];
  }, [assetType, tools.data, servers.data]);

  function canAdvance(): boolean {
    if (step === 1) return Boolean(assetType && assetId.trim());
    if (step === 2) return Boolean(riskTier);
    return true;
  }

  function onSubmit(): void {
    setActionError(null);
    submit
      .mutateAsync({
        asset_type: assetType,
        asset_id: assetId.trim(),
        risk_tier: riskTier,
        risk_notes: riskNotes.trim() || undefined,
      })
      .then((row) => {
        navigate(`/govern/approvals#${row.id}`);
      })
      .catch((err: unknown) => {
        setActionError(
          err instanceof ApiError
            ? `${err.status}: ${JSON.stringify(err.body)}`
            : (err as Error).message,
        );
      });
  }

  return (
    <>
      <PageHeader
        eyebrow="Discover"
        title="Request access"
        description="Three-step wizard. Submitting creates an approval row that the right reviewer WG sees in /govern/approvals."
      >
        <Link to="/access">
          <ActionButton icon={ArrowLeft}>Back to My Access</ActionButton>
        </Link>
      </PageHeader>

      <SectionCard
        icon={ShieldQuestion}
        title={`Step ${step} of 3 — ${step === 1 ? 'What' : step === 2 ? 'Why' : 'Review'}`}
        text={
          step === 1
            ? 'Pick the kind of request and the asset.'
            : step === 2
              ? 'Tell the reviewer why and how risky.'
              : 'Confirm and submit. The reviewer WG decides next.'
        }
      >
        {step === 1 && (
          <Step1
            assetType={assetType}
            setAssetType={setAssetType}
            assetId={assetId}
            setAssetId={setAssetId}
            suggestions={assetSuggestions}
          />
        )}
        {step === 2 && (
          <Step2
            riskTier={riskTier}
            setRiskTier={setRiskTier}
            riskNotes={riskNotes}
            setRiskNotes={setRiskNotes}
          />
        )}
        {step === 3 && (
          <Step3
            assetType={assetType}
            assetId={assetId}
            riskTier={riskTier}
            riskNotes={riskNotes}
          />
        )}

        {actionError && (
          <p style={{ color: '#c0392b', fontSize: 14, marginTop: 12 }} role="alert">
            <AlertTriangle size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
            {actionError}
          </p>
        )}

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: 20,
            gap: 8,
          }}
        >
          <button
            type="button"
            onClick={() => setStep(((s) => (s > 1 ? ((s - 1) as Step) : s))(step))}
            disabled={step === 1}
            style={btnStyle(step === 1)}
          >
            <ArrowLeft size={14} aria-hidden style={{ marginRight: 6 }} />
            Back
          </button>
          {step < 3 ? (
            <button
              type="button"
              onClick={() => canAdvance() && setStep(((s) => (s + 1) as Step)(step))}
              disabled={!canAdvance()}
              style={primaryBtnStyle(!canAdvance())}
            >
              Next
              <ArrowRight size={14} aria-hidden style={{ marginLeft: 6 }} />
            </button>
          ) : (
            <button
              type="button"
              onClick={onSubmit}
              disabled={submit.isPending}
              style={primaryBtnStyle(submit.isPending)}
            >
              <Check size={14} aria-hidden style={{ marginRight: 6 }} />
              {submit.isPending ? 'Submitting…' : 'Submit request'}
            </button>
          )}
        </div>
      </SectionCard>
    </>
  );
}

interface Suggestion {
  id: string;
  label: string;
}

function Step1({
  assetType,
  setAssetType,
  assetId,
  setAssetId,
  suggestions,
}: {
  assetType: AssetTypeOption;
  setAssetType: (a: AssetTypeOption) => void;
  assetId: string;
  setAssetId: (a: string) => void;
  suggestions: readonly Suggestion[];
}): JSX.Element {
  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <RequiredLegend />
      <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
        <legend style={{ fontSize: 14, marginBottom: 8 }}>
          What are you requesting?
          <Required />
        </legend>
        <div style={{ display: 'grid', gap: 8 }}>
          {ASSET_TYPES.map((opt) => (
            <label
              key={opt.value}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 8,
                cursor: 'pointer',
                fontSize: 14,
                padding: 8,
                border: '1px solid var(--line)',
                borderRadius: 8,
                background: assetType === opt.value ? 'rgba(143, 110, 70, 0.08)' : 'transparent',
              }}
            >
              <input
                type="radio"
                name="asset_type"
                value={opt.value}
                checked={assetType === opt.value}
                onChange={() => setAssetType(opt.value)}
                style={{ marginTop: 4 }}
              />
              <span>
                <strong>{opt.label}</strong>
                <br />
                <span className="subtle" style={{ fontSize: 13 }}>
                  {opt.help}
                </span>
              </span>
            </label>
          ))}
        </div>
      </fieldset>

      <label style={{ display: 'block' }}>
        <span style={{ display: 'block', fontSize: 14, marginBottom: 4 }}>
          Asset ID
          <Required />
        </span>
        <input
          type="text"
          value={assetId}
          onChange={(e) => setAssetId(e.target.value)}
          placeholder="server slug, tool id, or free-form identifier"
          list="access-asset-suggestions"
          style={inputStyle}
          required
        />
        {suggestions.length > 0 && (
          <datalist id="access-asset-suggestions">
            {suggestions.map((s) => (
              <option key={s.id} value={s.id}>
                {s.label}
              </option>
            ))}
          </datalist>
        )}
      </label>
    </div>
  );
}

function Step2({
  riskTier,
  setRiskTier,
  riskNotes,
  setRiskNotes,
}: {
  riskTier: (typeof RISK_TIERS)[number];
  setRiskTier: (r: (typeof RISK_TIERS)[number]) => void;
  riskNotes: string;
  setRiskNotes: (r: string) => void;
}): JSX.Element {
  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <label style={{ display: 'block' }}>
        <span style={{ display: 'block', fontSize: 14, marginBottom: 4 }}>
          Risk tier
          <Required />
        </span>
        <select
          value={riskTier}
          onChange={(e) => setRiskTier(e.target.value as (typeof RISK_TIERS)[number])}
          style={inputStyle}
          required
        >
          {RISK_TIERS.map((tier) => (
            <option key={tier} value={tier}>
              {tier}
            </option>
          ))}
        </select>
        <span className="subtle" style={{ fontSize: 13 }}>
          The reviewer escalation rules use this. Pick conservatively — reviewers can adjust.
        </span>
      </label>

      <label style={{ display: 'block' }}>
        <span style={{ display: 'block', fontSize: 14, marginBottom: 4 }}>
          Justification
          <Required />
        </span>
        <textarea
          value={riskNotes}
          onChange={(e) => setRiskNotes(e.target.value)}
          rows={5}
          placeholder="Why do you need this access? What dataset / task / project? How long do you expect to use it?"
          style={{ ...inputStyle, resize: 'vertical' }}
          required
        />
      </label>
    </div>
  );
}

function Step3({
  assetType,
  assetId,
  riskTier,
  riskNotes,
}: {
  assetType: AssetTypeOption;
  assetId: string;
  riskTier: string;
  riskNotes: string;
}): JSX.Element {
  return (
    <dl
      style={{
        display: 'grid',
        gridTemplateColumns: 'auto 1fr',
        rowGap: 8,
        columnGap: 16,
        margin: 0,
        fontSize: 15,
      }}
    >
      <dt className="subtle">Asset type</dt>
      <dd>
        <code>{assetType}</code>
      </dd>
      <dt className="subtle">Asset ID</dt>
      <dd>
        <code>{assetId || '—'}</code>
      </dd>
      <dt className="subtle">Risk tier</dt>
      <dd>{riskTier}</dd>
      <dt className="subtle">Justification</dt>
      <dd>{riskNotes || <span className="subtle">(none provided)</span>}</dd>
    </dl>
  );
}

const inputStyle: React.CSSProperties = {
  padding: '8px 10px',
  border: '1px solid var(--border, var(--line))',
  borderRadius: 8,
  fontSize: 15,
  background: 'transparent',
  color: 'inherit',
  width: '100%',
  boxSizing: 'border-box',
};

function btnStyle(disabled: boolean): React.CSSProperties {
  return {
    padding: '8px 14px',
    borderRadius: 8,
    border: '1px solid var(--line-strong)',
    background: 'rgba(255, 255, 255, 0.85)',
    color: 'inherit',
    fontSize: 14,
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1,
    display: 'inline-flex',
    alignItems: 'center',
  };
}

function primaryBtnStyle(disabled: boolean): React.CSSProperties {
  return {
    padding: '8px 14px',
    borderRadius: 8,
    border: '1px solid rgba(140, 21, 21, 0.3)',
    background: 'linear-gradient(135deg, #8c1515, #b83a24)',
    color: '#fff8ec',
    fontWeight: 650,
    fontSize: 14,
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.55 : 1,
    boxShadow: disabled ? 'none' : '0 12px 28px rgba(140, 21, 21, 0.22)',
    display: 'inline-flex',
    alignItems: 'center',
  };
}
